import React, { useState, useEffect, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import Modal from '../shared/Modal';
import { api } from '../../api/client';

const UncertaintiesTab = () => {
  const { activeSession, uncertainties, setUncertainties } = useAppContext(); // Remove currentUser
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newCategory, setNewCategory] = useState('Technology');
  const [isLoading, setIsLoading] = useState(false);

  // Use useCallback to memoize the function
  const loadUncertainties = useCallback(async () => {
    if (!activeSession) return;
    try {
      const data = await api.getUncertainties(activeSession.id);
      setUncertainties(data);
    } catch (error) {
      console.error('Failed to load uncertainties:', error);
    }
  }, [activeSession, setUncertainties]);

  useEffect(() => {
    loadUncertainties();
  }, [loadUncertainties]);

  const addUncertainty = async () => {
    if (!newTitle) return;

    setIsLoading(true);
    try {
      const newUncertainty = await api.createUncertainty({
        session_id: activeSession.id,
        title: newTitle,
        description: newDescription,
        category: newCategory,
      });
      
      setUncertainties([...uncertainties, newUncertainty]);
      setShowAddModal(false);
      setNewTitle('');
      setNewDescription('');
      setNewCategory('Technology');
    } catch (error) {
      console.error('Failed to create uncertainty:', error);
      alert('Failed to create uncertainty: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Key Uncertainties</h3>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center space-x-2 bg-[#01006A] text-white px-4 py-2 rounded-lg hover:bg-[#010059]"
        >
          <Plus className="w-5 h-5" />
          <span>Add Uncertainty</span>
        </button>
      </div>

      <div className="space-y-4">
        {uncertainties.map(unc => (
          <div key={unc.id} className="border border-gray-200 rounded-lg p-4 hover:border-[#6b6bc2] transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <h4 className="font-semibold text-gray-900">{unc.title}</h4>
                  <span className="px-2 py-1 bg-[#c0c0e6] text-[#010059] text-xs rounded">
                    {unc.category}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{unc.description}</p>
              </div>
            </div>
          </div>
        ))}

        {uncertainties.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <p>No uncertainties yet. Add your first uncertainty to begin.</p>
          </div>
        )}
      </div>

      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Uncertainty"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
              placeholder="e.g., AI Regulation Timeline"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
              rows="3"
              placeholder="Describe the uncertainty..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
            >
              <option>Technology</option>
              <option>Policy</option>
              <option>Economic</option>
              <option>Social</option>
              <option>Environmental</option>
            </select>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={addUncertainty}
              disabled={!newTitle || isLoading}
              className="flex-1 bg-[#01006A] text-white py-2 rounded-lg hover:bg-[#010059] disabled:bg-gray-300"
            >
              {isLoading ? 'Adding...' : 'Add'}
            </button>
            <button
              onClick={() => {
                setShowAddModal(false);
                setNewTitle('');
                setNewDescription('');
              }}
              className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UncertaintiesTab;